/**
 * DIFFICULTY TIERS.
 *
 * A shmup that only the developer can finish is a shmup nobody plays. The
 * genre's own answer — and the arcade original's — is that the *content* stays
 * identical and only the PRESSURE changes. The player should see the same
 * stage, the same enemies, the same boss, and the same story on every tier.
 *
 * What we scale is what makes a screen survivable:
 *
 *   bulletScale   — projectiles per volley. The single biggest lever, because
 *                   readability is a function of count more than speed.
 *   speedScale    — how fast fire crosses the screen, i.e. reaction time.
 *   cadenceScale  — seconds between volleys. Higher = more room to breathe.
 *   startLives/Shield — how many mistakes a run can absorb.
 *
 * What we deliberately DO NOT scale: enemy health, player damage, or score
 * values. Making a lower tier also make the player stronger turns "accessible"
 * into "trivial", and it corrupts score comparisons between tiers.
 */

export type DifficultyId = 'recruit' | 'standard' | 'ace'

export interface DifficultyTier {
  id: DifficultyId
  label: string
  blurb: string
  /** Multiplier on projectiles per enemy volley. */
  bulletScale: number
  /** Multiplier on enemy projectile speed. */
  speedScale: number
  /** Multiplier on the gap between volleys. >1 is slower, therefore easier. */
  cadenceScale: number
  startLives: number
  startShield: number
  startPower: number
}

export const DIFFICULTIES: Record<DifficultyId, DifficultyTier> = {
  /**
   * RECRUIT — built so the stage can actually be SEEN. Roughly 40% fewer
   * projectiles, appreciably slower, with real breathing room between volleys.
   * Still lethal if ignored; never a wall.
   */
  recruit: {
    id: 'recruit',
    label: 'RECRUIT',
    blurb: 'See the whole stage. Fewer rounds, more room.',
    bulletScale: 0.6,
    speedScale: 0.82,
    cadenceScale: 1.45,
    startLives: 5,
    startShield: 1,
    startPower: 3,
  },
  /** STANDARD — the authored experience. All bible values as written. */
  standard: {
    id: 'standard',
    label: 'STANDARD',
    blurb: 'The intended fight.',
    bulletScale: 1,
    speedScale: 1,
    cadenceScale: 1,
    startLives: 3,
    startShield: 0,
    startPower: 2,
  },
  /** ACE — for players who have cleared it and want the wall back. */
  ace: {
    id: 'ace',
    label: 'ACE',
    blurb: 'Denser, faster, unforgiving.',
    bulletScale: 1.3,
    speedScale: 1.12,
    cadenceScale: 0.82,
    startLives: 2,
    startShield: 0,
    startPower: 0,
  },
}

const STORAGE_KEY = 'spaceforce.difficulty'

/** Currently selected tier. Module-level so every system reads one source. */
let current: DifficultyTier = DIFFICULTIES.standard

export function getDifficulty(): DifficultyTier {
  return current
}

export function setDifficulty(id: DifficultyId): void {
  current = DIFFICULTIES[id] ?? DIFFICULTIES.standard
  try {
    localStorage.setItem(STORAGE_KEY, current.id)
  } catch {
    /* private browsing — the choice simply does not persist */
  }
}

/** Restore the last choice on load, defaulting to STANDARD. */
export function loadDifficulty(): DifficultyTier {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as DifficultyId | null
    if (saved && DIFFICULTIES[saved]) current = DIFFICULTIES[saved]
  } catch {
    /* ignore */
  }
  return current
}
